import { useTranslations } from "adapters/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  BenchmarkClienteFazendas,
  BenchmarkGrantBody,
  BenchmarkRevokeBody,
  BenchmarkUserStatusResponse,
} from "services/BenchmarkService";
import {
  clearUserManagementFormStateInStorage,
  getSelectedClientFromStorage,
  getUserManagementFormStateFromStorage,
  setUserManagementFormStateInStorage,
} from "shared/storageUtils";
import {
  UserPermission,
  type FarmAndRetreatLink,
  type NotifyFn,
} from "../../../../userManagement.types";
import { hasPermission } from "../../../../userManagement.utils";
import UserService from "../../../../userService";
import type { UserFormState } from "../userForm.types";
import {
  EmailError,
  FarmAndRetreatLinksError,
  LoginError,
  NameError,
  validateEmail,
  validateFarmAndRetreatLinks,
  validateLogin,
  validateName,
} from "../utils/userFormValidator";

type UseUserFormParams = {
  onNotify: NotifyFn;
  setPageLoading: (isLoading: boolean) => void;
  toManagementPage: () => void;
  userId?: number | null;
  onResetBenchmarkEditUser: () => void;
  onFetchBenchmarkAssociacoes: (clientId: number) => void;
  onFetchBenchmarkStatus: (
    email: string,
  ) => Promise<BenchmarkUserStatusResponse | null>;
  onGrantRevokeBenchmarkAccess: (
    email: string,
    body: BenchmarkGrantBody | BenchmarkRevokeBody,
  ) => Promise<void>;
  benchmarkAssociacoes: BenchmarkClienteFazendas[];
};

const initialState: UserFormState = {
  name: { value: "", error: null },
  login: { value: "", error: null },
  email: { value: "", error: null },
  canAccessApp: { value: false },
  canAccessPortal: { value: false },
  exportAndImportPastureCSV: { value: false },
  finalStock: { value: false },
  importMaps: { value: false },
  registerForageCycle: { value: false },
  farmAndRetreatLinks: { value: [], error: null },
  canAccessBenchmark: { value: false },
};

export function useUserForm({
  onNotify,
  setPageLoading,
  toManagementPage,
  userId,
  onResetBenchmarkEditUser,
  onFetchBenchmarkAssociacoes,
  onFetchBenchmarkStatus,
  onGrantRevokeBenchmarkAccess,
  benchmarkAssociacoes,
}: UseUserFormParams) {
  const isEditing = userId != null;
  const currentClientId = useMemo(() => getSelectedClientFromStorage(), []);

  const { t } = useTranslations();

  const [isLoading, setIsLoading] = useState<boolean>(isEditing);
  const [currentUser, setCurrentUser] = useState<{
    email: string;
    login: string;
  } | null>(null);
  const [formState, setFormState] = useState<UserFormState>(() => {
    const stateFromStorage =
      getUserManagementFormStateFromStorage<UserFormState>();
    return stateFromStorage != null
      ? { ...stateFromStorage }
      : { ...initialState };
  });

  const originalHasUserAccessRef = useRef<boolean>(false);

  const fetchUserData = async (userId: number, clientId: number) => {
    setIsLoading(true);

    const user = await UserService.getUserData(userId, clientId);

    const userPermissions = user?.permissoes ?? [];

    const farmsAndRetreatsLinks =
      user?.fazendas.map<FarmAndRetreatLink>((farm) => ({
        farm: { id: farm.idFazenda, name: farm.nome },
        retreats: user.retiros
          .filter((retreat) => retreat.idFazenda === farm.idFazenda)
          .map((retreat) => ({ id: retreat.idRetiro, name: retreat.nome })),
      })) ?? [];

    let hasUserAccess = false;

    const statusResponse = await onFetchBenchmarkStatus(user?.email ?? "");
    if (statusResponse != null) {
      hasUserAccess = statusResponse.hasUserAccess;
      onFetchBenchmarkAssociacoes(clientId);
    }

    originalHasUserAccessRef.current = hasUserAccess;

    setCurrentUser(
      user != null ? { email: user.email, login: user.login } : null,
    );
    setFormState({
      name: { value: user?.nome ?? "", error: null },
      login: { value: user?.login ?? "", error: null },
      email: { value: user?.email ?? "", error: null },
      canAccessApp: {
        value: hasPermission(UserPermission.CanAccessApp, userPermissions),
      },
      canAccessPortal: {
        value: hasPermission(UserPermission.CanAccessPortal, userPermissions),
      },
      exportAndImportPastureCSV: {
        value: hasPermission(
          UserPermission.ExportAndImportPastureCSV,
          userPermissions,
        ),
      },
      finalStock: {
        value: hasPermission(UserPermission.FinalStock, userPermissions),
      },
      importMaps: {
        value: hasPermission(UserPermission.ImportMaps, userPermissions),
      },
      registerForageCycle: {
        value: hasPermission(
          UserPermission.RegisterForageCycle,
          userPermissions,
        ),
      },
      farmAndRetreatLinks: { value: farmsAndRetreatsLinks, error: null },
      canAccessBenchmark: { value: hasUserAccess },
    });

    setIsLoading(false);
  };

  useEffect(() => {
    if (userId == null || currentClientId == null) return;

    fetchUserData(userId, currentClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    return () => {
      onResetBenchmarkEditUser();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeForm = <T extends keyof UserFormState>(
    field: T,
    value: UserFormState[T],
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: { ...prevState[field], ...value },
    }));
  };

  const handleAddFarmAndRetreatLink = useCallback(
    ({ farm, retreats }: FarmAndRetreatLink) => {
      setFormState((prevState) => ({
        ...prevState,
        farmAndRetreatLinks: {
          ...prevState.farmAndRetreatLinks,
          value: [...prevState.farmAndRetreatLinks.value, { farm, retreats }],
        },
      }));
    },
    [],
  );

  const handleRemoveFarmAndRetreatLink = useCallback((farmId: number) => {
    setFormState((prevState) => ({
      ...prevState,
      farmAndRetreatLinks: {
        ...prevState.farmAndRetreatLinks,
        value: [...prevState.farmAndRetreatLinks.value].filter(
          (link) => link.farm.id !== farmId,
        ),
      },
    }));
  }, []);

  const loginErrorToMessage: Record<LoginError, string> = {
    [LoginError.InvalidFormat]: t("user:invalid-user-login-format"),
    [LoginError.AlreadyExists]: t("user:login-already-exists"),
  };

  const emailErrorToMessage: Record<EmailError, string> = {
    [EmailError.InvalidFormat]: t("user:invalid-user-email-format"),
    [EmailError.AlreadyExists]: t("user:email-already-exists"),
  };

  const nameErrorToMessage: Record<NameError, string> = {
    [NameError.Required]: t("common:validation.required-field"),
  };

  const farmAndRetreatLinksErrorToMessage: Record<
    FarmAndRetreatLinksError,
    string
  > = {
    [FarmAndRetreatLinksError.Required]: t("common:validation.required-field"),
  };

  const handleBlur = async (field: string) => {
    if (field === "login") {
      const { error } = await validateLogin({
        currentLogin: currentUser?.login ?? null,
        isEditing,
        newLogin: formState.login.value,
      });

      const message = error != null ? loginErrorToMessage[error] : null;

      setFormState((prevState) => ({
        ...prevState,
        login: { ...prevState.login, error: message },
      }));
      return;
    }

    if (field === "email") {
      const { error } = await validateEmail({
        currentEmail: currentUser?.email ?? null,
        isEditing,
        newEmail: formState.email.value,
      });

      const message = error != null ? emailErrorToMessage[error] : null;

      setFormState((prevState) => ({
        ...prevState,
        email: { ...prevState.email, error: message },
      }));
      return;
    }
  };

  const validateForm = async () => {
    const { isValid: isNameValid, error: nameError } = validateName(
      formState.name.value,
    );
    const { isValid: isLoginValid, error: loginError } = await validateLogin({
      currentLogin: currentUser?.login ?? null,
      isEditing,
      newLogin: formState.login.value,
    });
    const { isValid: isEmailValid, error: emailError } = await validateEmail({
      currentEmail: currentUser?.email ?? null,
      isEditing,
      newEmail: formState.email.value,
    });

    const {
      isValid: isFarmAndRetreatsLinksValid,
      error: farmAndRetreatLinksError,
    } = validateFarmAndRetreatLinks(formState.farmAndRetreatLinks.value);

    const newFormState: UserFormState = { ...formState };

    newFormState.name.error =
      nameError != null ? nameErrorToMessage[nameError] : null;
    newFormState.login.error =
      loginError != null ? loginErrorToMessage[loginError] : null;
    newFormState.email.error =
      emailError != null ? emailErrorToMessage[emailError] : null;
    newFormState.farmAndRetreatLinks.error =
      farmAndRetreatLinksError != null
        ? farmAndRetreatLinksErrorToMessage[farmAndRetreatLinksError]
        : null;

    return {
      isValid:
        isNameValid &&
        isLoginValid &&
        isEmailValid &&
        isFarmAndRetreatsLinksValid,
      newFormState,
    };
  };

  const handleSubmit = async () => {
    const saveFarmsAndPermissions = async (
      userData: { email: string; login: string; name: string },
      permissions: Record<UserPermission, boolean>,
      clientId: number,
    ) => {
      if (isEditing) {
        await UserService.editUserWithRelations(
          userId,
          userData,
          permissions,
          formState.farmAndRetreatLinks.value,
        );
      } else {
        await UserService.createUserWithRelations(
          userData,
          permissions,
          formState.farmAndRetreatLinks.value,
          clientId,
        );
      }
    };

    const buildGrantBody = (userName: string): BenchmarkGrantBody => {
      const currentFarmNames = new Set(
        formState.farmAndRetreatLinks.value.map((l) => l.farm.name),
      );
      const associations = benchmarkAssociacoes
        .map((assoc) => ({
          customer_name: assoc.nomeCliente,
          farm_names: assoc.fazendas
            .filter((f) => f.has_access && currentFarmNames.has(f.name))
            .map((f) => f.name),
        }))
        .filter((assoc) => assoc.farm_names.length > 0);
      return { action: "grant", name: userName, associations };
    };

    const saveBenchmarkAccess = async (userData: {
      email: string;
      name: string;
    }) => {
      const benchmarkChanged =
        formState.canAccessBenchmark.value !== originalHasUserAccessRef.current;
      if (!benchmarkChanged) return;

      const body: BenchmarkGrantBody | BenchmarkRevokeBody = formState
        .canAccessBenchmark.value
        ? buildGrantBody(userData.name)
        : { action: "revoke" };

      try {
        await onGrantRevokeBenchmarkAccess(userData.email, body);
      } catch (error) {
        setFormState((prev) => ({
          ...prev,
          canAccessBenchmark: { value: originalHasUserAccessRef.current },
        }));
        const apiMessage =
          error instanceof Error && error.message ? error.message : null;
        const toastMessage = [
          t("benchmark:permissions-saved-benchmark-error"),
          apiMessage,
        ]
          .filter(Boolean)
          .join(" ");
        onNotify(toastMessage, "error");
      }
    };

    try {
      setPageLoading(true);

      const { isValid, newFormState } = await validateForm();
      setUserManagementFormStateInStorage(newFormState);

      if (!isValid) {
        setFormState(newFormState);
        throw new Error(t("common:validation.fill-all-required-fields"));
      }

      if (currentClientId == null) {
        throw new Error(t("common:validation.missing-storage-values"));
      }

      const userData = {
        email: formState.email.value.trim().toLowerCase(),
        login: formState.login.value.trim().toLowerCase(),
        name: formState.name.value,
      };

      const permissions: Record<UserPermission, boolean> = {
        [UserPermission.CanAccessApp]: formState.canAccessApp.value,
        [UserPermission.CanAccessPortal]: formState.canAccessPortal.value,
        [UserPermission.ExportAndImportPastureCSV]:
          formState.exportAndImportPastureCSV.value,
        [UserPermission.FinalStock]: formState.finalStock.value,
        [UserPermission.ImportMaps]: formState.importMaps.value,
        [UserPermission.RegisterForageCycle]:
          formState.registerForageCycle.value,
        [UserPermission.CanAccessBenchmark]: false,
      };

      await saveFarmsAndPermissions(userData, permissions, currentClientId);
      await saveBenchmarkAccess(userData);

      onNotify(
        isEditing
          ? t("user:update-user-success")
          : t("user:create-new-user-success"),
        "success",
      );
      clearUserManagementFormStateInStorage();
      toManagementPage();
    } catch (error) {
      window.scrollTo(0, 0);
      onNotify(
        error instanceof Error
          ? error.message
          : t("user:create-new-user-error"),
        "error",
      );
    } finally {
      setPageLoading(false);
    }
  };

  const handleCancel = () => {
    clearUserManagementFormStateInStorage();
    toManagementPage();
  };

  return {
    formState,
    handleAddFarmAndRetreatLink,
    handleBlur,
    handleCancel,
    handleChangeForm,
    handleRemoveFarmAndRetreatLink,
    handleSubmit,
    isEditing,
    isLoading,
  };
}
